import type { IconType } from 'react-icons';
import { TbListDetails } from 'react-icons/tb';

import { WORKSPACE_RESOURCE_ICON_REGISTRY } from '@/shared/components/workspace-resources';

export const HOMEPAGE_VIEW_KEYS = ['default', 'dashboard', 'tasks'] as const;

export type HomepageViewKey = (typeof HOMEPAGE_VIEW_KEYS)[number];

interface HomepageViewDefinition {
    icon: IconType;
    label: string;
    searchTerms: string;
}

export const HOMEPAGE_VIEW_REGISTRY = {
    default: {
        icon: TbListDetails,
        label: 'Default Homepage',
        searchTerms: 'default homepage recent favorites shared',
    },
    dashboard: {
        icon: WORKSPACE_RESOURCE_ICON_REGISTRY.dashboard,
        label: 'Dashboard',
        searchTerms: 'dashboard',
    },
    tasks: {
        icon: WORKSPACE_RESOURCE_ICON_REGISTRY.task,
        label: 'Tasks',
        searchTerms: 'tasks work to do',
    },
} satisfies Record<HomepageViewKey, HomepageViewDefinition>;

interface HomepagePreference {
    dashboardId?: string | null;
    view?: HomepageViewKey;
}

interface ResolveHomepageViewOptions {
    dashboardIdQuery?: string | null;
    preference?: HomepagePreference;
}

interface ResolvedHomepageView {
    dashboardId: string | null;
    view: HomepageViewKey;
}

/** Resolves legacy dashboard-only preferences and explicit homepage views through one contract. */
export const resolveHomepageView = ({
    dashboardIdQuery,
    preference,
}: ResolveHomepageViewOptions): ResolvedHomepageView => {
    if (dashboardIdQuery) {
        return { dashboardId: dashboardIdQuery, view: 'dashboard' };
    }

    const dashboardId = preference?.dashboardId ?? null;

    if (preference?.view === 'tasks') {
        return { dashboardId, view: 'tasks' };
    }

    if (preference?.view === 'default') {
        return { dashboardId, view: 'default' };
    }

    return dashboardId ? { dashboardId, view: 'dashboard' } : { dashboardId: null, view: 'default' };
};
